import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import DocumentView from './DocumentView';
import ReprocessInPlaceModal from '../Documents/ReprocessInPlaceModal';
import { mapDocumentToFicha, fieldEditsFromFicha } from '../../utils/documentFichaMapper';
import { useArcaFeature } from '../../hooks/useArcaFeature';
import { useApi } from '../../hooks/useApi';
import { fetchCanonicalDocumentRecord } from '../../utils/documentRedirect';
import { buildDocumentByIdQuery } from '../../utils/documentDetailQuery';
import {
  inPlaceReprocessToastMessage,
  isDocumentReprocessing,
  isInPlaceReprocessable,
  runInPlaceReprocess,
} from '../../utils/documentReprocess';
import './DocumentView.css';

/**
 * Modal shell SPEC-12 — reemplaza InvoiceModal / UnclassifiedDocumentModal para comprobantes.
 */
export default function DocumentViewModal({
  isOpen,
  onClose,
  documentRecord,
  canEdit = false,
  onClassify,
  reprocessDocTypes = [],
  onRecordChange,
}) {
  const { callApi } = useApi();
  const { enabled: arcaEnabled, canConstatar } = useArcaFeature();
  const allowArcaAction = canConstatar || canEdit;
  const [record, setRecord] = useState(documentRecord);
  const [reprocessModalOpen, setReprocessModalOpen] = useState(false);
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setRecord(documentRecord);
  }, [documentRecord]);

  const ficha = useMemo(
    () => (record ? mapDocumentToFicha(record, { canEdit }) : null),
    [record, canEdit],
  );

  const refreshRecord = useCallback(async () => {
    const docId = record?.document_id;
    if (!docId) return null;
    const updated = await fetchCanonicalDocumentRecord(
      callApi,
      buildDocumentByIdQuery,
      docId,
    );
    if (updated) {
      setRecord(updated);
      onRecordChange?.(updated);
    }
    return updated;
  }, [callApi, onRecordChange, record?.document_id]);

  const reprocessing = isReprocessing || isDocumentReprocessing(record || ficha);

  useEffect(() => {
    if (!isOpen || !record?.document_id || !reprocessing || isReprocessing) return undefined;
    const timer = setInterval(() => {
      refreshRecord();
    }, 3000);
    return () => clearInterval(timer);
  }, [isOpen, isReprocessing, record?.document_id, reprocessing, refreshRecord]);

  const reprocessable = canEdit
    && isInPlaceReprocessable(record || ficha, { canEdit })
    && !reprocessing;

  const executeInPlaceReprocess = async () => {
    const docId = record?.document_id;
    if (!docId) return;
    setReprocessLoading(true);
    setIsReprocessing(true);
    try {
      const result = await runInPlaceReprocess(callApi, docId, {
        onTick: async () => refreshRecord(),
      });
      setReprocessModalOpen(false);
      setToast({
        severity: result?.status === 'QUEUED' ? 'success' : 'error',
        message: inPlaceReprocessToastMessage(result),
      });
      if (result?.status === 'QUEUED') {
        await refreshRecord();
      }
    } catch (err) {
      setToast({
        severity: 'error',
        message: err?.message || 'Error al reprocesar documento',
      });
    } finally {
      setReprocessLoading(false);
      setIsReprocessing(false);
    }
  };

  if (!isOpen || !ficha) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      <div className="dv-modal-overlay" onClick={handleOverlayClick}>
        <div className="dv-modal-container" onClick={(e) => e.stopPropagation()}>
          <DocumentView
            ficha={ficha}
            onClose={onClose}
            onClassify={onClassify}
            reprocessDocTypes={reprocessDocTypes}
            onFichaChange={(next) => {
              if (next?.doc_id) {
                setRecord((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    field_edits: fieldEditsFromFicha(next),
                  };
                });
              }
            }}
            arcaEnabled={arcaEnabled}
            canConstatar={allowArcaAction}
            reprocessable={reprocessable}
            onReprocess={() => setReprocessModalOpen(true)}
            reprocessLoading={reprocessLoading}
            isReprocessing={reprocessing}
          />
        </div>
      </div>

      <ReprocessInPlaceModal
        isOpen={reprocessModalOpen}
        filename={ficha?.filename}
        loading={reprocessLoading}
        onConfirm={executeInPlaceReprocess}
        onCancel={() => {
          if (!reprocessLoading) setReprocessModalOpen(false);
        }}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
